import { Autocomplete, Grid, InputLabel, MenuItem, TextField, Typography } from '@mui/material';
import React, { Fragment, ReactNode, useEffect } from 'react';
import { DateTimeFormat, getLocalDateTime, WO2Select, WO2ToggleSwitch } from 'src/common';
import { BenchmarkItem } from 'src/features/clients/client/performance/store/types';
import { AvailablePeriods } from 'src/features/clients/client/reportsV2/components/MultiplePeriodSelector';
import { YearTypeSelector } from 'src/features/clients/client/reportsV2/components/YearTypeSelector';
import {
  AssetAllocation,
  AssetClassPerformance,
  CashTransactions,
  ContributionsAndWithdrawals,
  CurrencyExposure,
  DateRangeBaseRequest,
  Expenses,
  GroupByIncomeReceived,
  GroupByPortfolioValuation,
  GroupBySecurityContribution,
  GroupBySecurityPerformance,
  GroupByTransactionHistory,
  GroupConsolidatedOption,
  HoldingsVsTargetVariance,
  IncomeReceived,
  IncomeReport,
  InitiateReportRequest,
  InvestmentIncomeTax,
  MdaReportType,
  MdaReportTypes,
  PerformanceDataType,
  PerformanceMethodology,
  PortfolioPerformance,
  PortfolioSnapshot,
  PortfolioValuation,
  ProjectedIncomeCashflow,
  RealisedCapitalGainsAndLosses,
  SecurityContribution,
  SecurityPerformance,
  ShowReceivablesAsType,
  SortByCashTransactions,
  SortByIncomeReceived,
  SortByPortfolioValuation,
  SortByTransactionHistory,
  TaxSummary,
  TransactionHistory,
  UnrealisedCapitalGainsAndLosses,
  UnrealisedNonCGTGainsAndLosses,
} from 'src/features/clients/client/reportsV2/store/types';
import { Props } from '../container';
import { ReportingBatchReportCard } from './ReportingBatchReportCard';

export interface ReportingBatchReportViewProps extends Props {
  request?: InitiateReportRequest;
  fullReports: boolean;
}

interface DateRangeCardProps {
  name: string;
  date: DateRangeBaseRequest;
}

interface ToggleProps {
  checked: boolean;
  name: string;
}

export const ReportingBatchReportView = (props: ReportingBatchReportViewProps): JSX.Element | null => {
  const request = props.request;

  useEffect(() => {
    props.fetchBenchmarks(-1);
  }, []);

  if (!request) {
    return <Typography>No report(s) have been selected for this batch.</Typography>;
  }

  const mergeOrder = Array.from(new Set(request.mergeReportOrder));
  const reports: { id: number; component: ReactNode }[] = [];

  if (request.assetAllocation) {
    reports.push({
      id: MdaReportTypes.AssetAllocation.id,
      component: <AssetAllocationCard {...request.assetAllocation} />,
    });
  }
  if (request.assetClassPerformance) {
    reports.push({
      id: MdaReportTypes.AssetClassPerformance.id,
      component: <AssetClassPerformanceCard {...request.assetClassPerformance} />,
    });
  }
  if (request.cashTransactions) {
    reports.push({
      id: MdaReportTypes.CashTransactions.id,
      component: <CashTransactionCard {...request.cashTransactions} />,
    });
  }
  if (request.contributionsAndWithdrawals) {
    reports.push({
      id: MdaReportTypes.ContributionsAndWithdrawals.id,
      component: <ContributionsAndWithdrawalsCard {...request.contributionsAndWithdrawals} />,
    });
  }
  if (request.currencyExposure) {
    reports.push({
      id: MdaReportTypes.CurrencyExposure.id,
      component: <CurrencyExposureCard {...request.currencyExposure} />,
    });
  }
  if (request.expenses) {
    reports.push({
      id: MdaReportTypes.Expenses.id,
      component: <ExpensesCard {...request.expenses} />,
    });
  }
  if (request.holdingsVsTargetVariance) {
    reports.push({
      id: MdaReportTypes.HoldingsVsTargetVariance.id,
      component: <HoldingsVsTargetVarianceCard {...request.holdingsVsTargetVariance} />,
    });
  }
  if (request.incomeReceived) {
    reports.push({
      id: MdaReportTypes.IncomeReceived.id,
      component: <IncomeReceivedCard {...request.incomeReceived} />,
    });
  }
  if (request.incomeReport) {
    reports.push({
      id: MdaReportTypes.IncomeReport.id,
      component: <IncomeCard {...request.incomeReport} />,
    });
  }
  if (request.investmentIncomeTaxComponent) {
    reports.push({
      id: MdaReportTypes.InvestmentIncomeTax.id,
      component: <InvestmentIncomeTaxCard {...request.investmentIncomeTaxComponent} />,
    });
  }
  if (request.portfolioPerformance) {
    reports.push({
      id: MdaReportTypes.PortfolioPerformance.id,
      component: <PortfolioPerformanceCard benchmarks={props.benchmarks} portfolioPerformance={request.portfolioPerformance} />,
    });
  }
  if (request.portfolioSnapshot) {
    reports.push({
      id: MdaReportTypes.PortfolioSnapshot.id,
      component: <PortfolioSnapshotCard benchmarks={props.benchmarks} portfolioSnapshot={request.portfolioSnapshot} />,
    });
  }
  if (request.portfolioValuation) {
    reports.push({
      id: MdaReportTypes.PortfolioValuation.id,
      component: <PortfolioValuationCard {...request.portfolioValuation} />,
    });
  }
  if (request.projectedIncomeCashflow) {
    reports.push({
      id: MdaReportTypes.ProjectedIncomeCashflow.id,
      component: <ProjectedIncomeCashflowCard {...request.projectedIncomeCashflow} />,
    });
  }
  if (request.realisedCapitalGainsAndLosses) {
    reports.push({
      id: MdaReportTypes.RealisedCapitalGainsAndLosses.id,
      component: <RealisedCapitalGainsAndLossesCard {...request.realisedCapitalGainsAndLosses} />,
    });
  }
  if (request.securityContribution) {
    reports.push({
      id: MdaReportTypes.SecurityContribution.id,
      component: <SecurityContributionCard {...request.securityContribution} />,
    });
  }
  if (request.securityPerformance) {
    reports.push({
      id: MdaReportTypes.SecurityPerformance.id,
      component: <SecurityPerformanceCard {...request.securityPerformance} />,
    });
  }
  if (request.taxInvoice) {
    reports.push({
      id: MdaReportTypes.TaxInvoice.id,
      component: <DateRangeCard date={request.taxInvoice} name={MdaReportType.TaxInvoice} />,
    });
  }
  if (request.taxSummary) {
    reports.push({
      id: MdaReportTypes.TaxSummary.id,
      component: <TaxSummaryCard {...request.taxSummary} />,
    });
  }
  if (request.transactionHistory) {
    reports.push({
      id: MdaReportTypes.TransactionHistory.id,
      component: <TransactionHistoryCard {...request.transactionHistory} />,
    });
  }
  if (request.unrealisedCapitalGainsAndLosses) {
    reports.push({
      id: MdaReportTypes.UnrealisedCapitalGainsAndLosses.id,
      component: <UnrealisedCapitalGainsAndLossesCard {...request.unrealisedCapitalGainsAndLosses} />,
    });
  }
  if (request.unrealisedNonCGT) {
    reports.push({
      id: MdaReportTypes.UnrealisedNonCgt.id,
      component: <UnrealisedNonCGTCard {...request.unrealisedNonCGT} />,
    });
  }
  if (request.uXchangeTaxStatement) {
    reports.push({
      id: MdaReportTypes.UXChangeTaxStatement.id,
      component: <DateRangeCard date={request.uXchangeTaxStatement} name={MdaReportType.UXChangeTaxStatement} />,
    });
  }

  const orderedReports = mergeOrder
    .map((id: number) => reports.find((report) => report.id === id))
    .filter((report): report is { id: number; component: ReactNode } => !!report);

  return (
    <Grid item container direction="column" spacing={2}>
      {orderedReports.map((report) => (
        <Fragment key={report.id}>{report.component}</Fragment>
      ))}
    </Grid>
  );
};

const RealisedCapitalGainsAndLossesCard = (realisedCapitalGainsAndLosses: RealisedCapitalGainsAndLosses) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.RealisedCapitalGainsAndLosses} from ${getLocalDateTime(
      realisedCapitalGainsAndLosses.fromDate,
      DateTimeFormat.DayDate
    )} to ${getLocalDateTime(realisedCapitalGainsAndLosses.toDate, DateTimeFormat.DayDate)}`}
  >
    <Toggle checked={realisedCapitalGainsAndLosses.includeSummary} name="Include Summary" />
    <Toggle checked={realisedCapitalGainsAndLosses.excludeFxGainsLosses} name="Exclude FX Gains/Losses" />
    <Toggle checked={realisedCapitalGainsAndLosses.showCostBaseAdjustments} name="Show Cost Base Adjustments" />
  </ReportingBatchReportCard>
);

const UnrealisedCapitalGainsAndLossesCard = (unrealisedCapitalGainsAndLosses: UnrealisedCapitalGainsAndLosses) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.UnrealisedCapitalGainsAndLosses} from ${getLocalDateTime(
      unrealisedCapitalGainsAndLosses.asAtDate,
      DateTimeFormat.DayDate
    )}`}
  >
    <Toggle checked={unrealisedCapitalGainsAndLosses.excludeFxGainsLosses} name="Exclude FX Gains/Losses" />
    <Toggle checked={unrealisedCapitalGainsAndLosses.showCostBaseAdjustments} name="Show Cost Base Adjustments" />
  </ReportingBatchReportCard>
);

const UnrealisedNonCGTCard = (unrealisedNonCgt: UnrealisedNonCGTGainsAndLosses) => (
  <ReportingBatchReportCard title={`${MdaReportType.UnrealisedNonCGT} from ${getLocalDateTime(unrealisedNonCgt.asAtDate, DateTimeFormat.DayDate)}`}>
    <Toggle checked={unrealisedNonCgt.excludeFxGainsLosses} name="Exclude FX Gains/Losses" />
  </ReportingBatchReportCard>
);

const TransactionHistoryCard = (transactionHistory: TransactionHistory) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.TransactionHistory} from ${getLocalDateTime(transactionHistory.fromDate, DateTimeFormat.DayDate)} to ${getLocalDateTime(
      transactionHistory.toDate,
      DateTimeFormat.DayDate
    )}`}
  >
    <Toggle checked={transactionHistory.excludeCash} name="Exclude Cash" />
    <Toggle checked={transactionHistory.includeDistribution} name="Include Distribution" />
    <Toggle checked={transactionHistory.excludeAssetDescription} name="Exclude Asset Description" />
    <Toggle checked={transactionHistory.excludeAccounts} name="Exclude Accounts" />
    <Toggle checked={transactionHistory.includeBrokerage} name="Include Brokerage" />
    <Toggle checked={transactionHistory.includeGST} name="Include GST" />
    <Toggle checked={transactionHistory.includeOtherCharges} name="Include Other Charges" />
    <Grid item container spacing={2}>
      <Grid item>
        <InputLabel id="sort-by-id">Sort by</InputLabel>
        <WO2Select disabled labelId="sort-by-id" label="sort by" value={transactionHistory.sortBy}>
          <MenuItem value={SortByTransactionHistory.Account}>Account</MenuItem>
          <MenuItem value={SortByTransactionHistory.TradeDate}>Trade Date</MenuItem>
          <MenuItem value={SortByTransactionHistory.SecurityName}>Security Name</MenuItem>
          <MenuItem value={SortByTransactionHistory.TransactionType}>Transaction Type</MenuItem>
        </WO2Select>
      </Grid>
      <Grid item>
        <InputLabel id="group-by-id">Group by</InputLabel>
        <WO2Select disabled labelId="group-by-id" label="group by" value={transactionHistory.groupBy}>
          <MenuItem value={GroupByTransactionHistory.Account}>Account</MenuItem>
          <MenuItem value={GroupByTransactionHistory.TradeDate}>Trade Date</MenuItem>
          <MenuItem value={GroupByTransactionHistory.SecurityName}>Security Name</MenuItem>
          <MenuItem value={GroupByTransactionHistory.TransactionType}>Transaction Type</MenuItem>
        </WO2Select>
      </Grid>
      <GroupingOption value={transactionHistory.groupOption} />
    </Grid>
  </ReportingBatchReportCard>
);

const SecurityContributionCard = (securityContribution: SecurityContribution) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.SecurityContribution} from ${getLocalDateTime(
      securityContribution.fromDate,
      DateTimeFormat.DayDate
    )} to ${getLocalDateTime(securityContribution.toDate, DateTimeFormat.DayDate)}`}
  >
    <Toggle checked={securityContribution.excludeFrankingCredits} name="Exclude Franking Credits" />
    <Toggle checked={securityContribution.showAnnualisedReturns} name="Show Annualised Returns" />
    <Toggle checked={securityContribution.applyExclusionList} name="Apply Exclusion List" />
    <Toggle checked={securityContribution.excludeCapitalIncomeReturns} name="Exclude Capital Income Returns" />
    <Toggle checked={securityContribution.includeDetailTable} name="Include Detail Table" />
    <Grid item container spacing={2}>
      <Grid item>
        <InputLabel id="group-by-id">Group by</InputLabel>
        <WO2Select disabled value={securityContribution.groupBy}>
          <MenuItem value={GroupBySecurityContribution.AssetClass}>Asset Class</MenuItem>
          <MenuItem value={GroupBySecurityContribution.Gics}>GICS</MenuItem>
        </WO2Select>
      </Grid>
      <GroupingOption value={securityContribution.groupOption} />
    </Grid>
  </ReportingBatchReportCard>
);

const PortfolioValuationCard = (portfolioValuation: PortfolioValuation) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.PortfolioValuation} from ${getLocalDateTime(portfolioValuation.asAtDate, DateTimeFormat.DayDate)}`}
  >
    <Toggle checked={portfolioValuation.excludeCostBase} name="Exclude Cost Base" />
    <Toggle checked={portfolioValuation.excludeMarket} name="Exclude Market" />
    <Toggle checked={portfolioValuation.excludeSecurityType} name="Exclude Security Type" />
    <Toggle checked={portfolioValuation.excludeInterestAccrual} name="Exclude Interest Accruals" />
    <Toggle checked={portfolioValuation.excludeGICS} name="Exclude GICS" />
    <Toggle checked={portfolioValuation.excludeGainsLosses} name="Exclude Gains/Losses" />
    <Toggle checked={portfolioValuation.excludeAvgUnitCost} name="Exclude Avg. Unit Cost" />
    <Toggle checked={portfolioValuation.excludeEstIncomeYield} name="Exclude Est. Income & Yield" />
    <Toggle checked={portfolioValuation.excludeEstGrossIncomeYield} name="Exclude Est. Gross Income & Yield" />
    <Toggle checked={portfolioValuation.excludeFXGainsLosses} name="Exclude FX Gains/Losses" />
    <Toggle checked={portfolioValuation.excludeLocalPriceValue} name="Exclude Local Price & Value" />
    <Toggle checked={portfolioValuation.excludePendingCashDetails} name="Exclude Pending Cash Details" />
    <Toggle checked={portfolioValuation.excludeTotalReturn} name="Exclude Total Returns since inception" />
    <Toggle checked={portfolioValuation.applyExclusionList} name="Apply Exclusion List" />
    <Toggle checked={portfolioValuation.excludePriceDate} name="Exclude Price Date" />
    <Grid item container spacing={2}>
      <Grid item>
        <InputLabel id="sort-by-id">Sort by</InputLabel>
        <WO2Select disabled labelId="sort-by-id" label="sort by" value={portfolioValuation.sortBy}>
          <MenuItem value={SortByPortfolioValuation.SecurityType}>Security Type</MenuItem>
          <MenuItem value={SortByPortfolioValuation.SecurityName}>Security Name</MenuItem>
          <MenuItem value={SortByPortfolioValuation.Gics}>GICS</MenuItem>
          <MenuItem value={SortByPortfolioValuation.ValueDescend}>Value (largest to smallest)</MenuItem>
          <MenuItem value={SortByPortfolioValuation.ValueAscend}>Value (smallest to largest)</MenuItem>
        </WO2Select>
      </Grid>
      <Grid item>
        <InputLabel id="group-by-id">Group by</InputLabel>
        <WO2Select disabled labelId="group-by-id" label="group by" value={portfolioValuation.groupBy}>
          <MenuItem value={GroupByPortfolioValuation.AssetClass}>Asset Class</MenuItem>
          <MenuItem value={GroupByPortfolioValuation.Gics}>GICS</MenuItem>
          <MenuItem value={GroupByPortfolioValuation.Portfolio}>Portfolio</MenuItem>
          <MenuItem value={GroupByPortfolioValuation.Model}>Model</MenuItem>
        </WO2Select>
      </Grid>
      <GroupingOption value={portfolioValuation.groupOption} />
    </Grid>
  </ReportingBatchReportCard>
);

const PortfolioPerformanceCard = ({
  benchmarks,
  portfolioPerformance,
}: {
  benchmarks: BenchmarkItem[];
  portfolioPerformance: PortfolioPerformance;
}) => {
  const multiPeriods = portfolioPerformance?.multiPeriods ?? [];
  return (
    <ReportingBatchReportCard
      title={`${MdaReportType.PortfolioPerformance} from ${getLocalDateTime(
        portfolioPerformance.fromDate,
        DateTimeFormat.DayDate
      )} to ${getLocalDateTime(portfolioPerformance.toDate, DateTimeFormat.DayDate)}`}
    >
      {multiPeriods.length > 0 && (
        <Grid item>
          <Autocomplete
            noOptionsText="No items to select"
            fullWidth
            multiple
            value={multiPeriods}
            options={multiPeriods}
            getOptionLabel={(option) => {
              const available = AvailablePeriods.find((period) => period.periodType.name === option.periodType);
              return available?.displayName ?? '';
            }}
            renderInput={(params) => <TextField {...params} margin="normal" variant="outlined" fullWidth />}
          />
        </Grid>
      )}
      {portfolioPerformance.benchmarkId !== -1 && benchmarks && (
        <Grid item>
          <InputLabel id="benchmark">Benchmark</InputLabel>
          <TextField fullWidth disabled label={benchmarks.find((benchmark) => benchmark.id === portfolioPerformance.benchmarkId)?.name} />
        </Grid>
      )}
      <Toggle checked={portfolioPerformance.showAnnualisedReturns} name="Show Annualised Returns" />
      <Toggle checked={portfolioPerformance.excludeFrankingCredits} name="Exclude Franking Credits" />
      <Toggle checked={portfolioPerformance.applyExclusionList} name="Apply Exclusion List" />
      <Toggle checked={portfolioPerformance.includeAfterFeesReturn} name="Include After Fees Return" />
      <Toggle checked={portfolioPerformance.includeNetContributions} name="Include Net Contributions" />
      <Toggle checked={portfolioPerformance.primaryBenchmark} name="Use Primary Benchmark" />
      <Toggle checked={portfolioPerformance.secondaryBenchmark} name="Use Secondary Benchmark" />
      <Grid item container spacing={2}>
        <Grid item>
          <InputLabel id="performance-methodology">Performance Methodology</InputLabel>
          <WO2Select disabled labelId="performance-methodology" label="performance methodology" value={portfolioPerformance.performanceMethodology}>
            <MenuItem value={PerformanceMethodology.TimeWeighted}>Time Weighted Return</MenuItem>
            <MenuItem value={PerformanceMethodology.ReturnOnAverage}>Return on Average Value</MenuItem>
          </WO2Select>
        </Grid>
        <GroupingOption value={portfolioPerformance.groupOption} />
      </Grid>
    </ReportingBatchReportCard>
  );
};

const PortfolioSnapshotCard = ({ benchmarks, portfolioSnapshot }: { benchmarks: BenchmarkItem[]; portfolioSnapshot: PortfolioSnapshot }) => {
  const multiPeriods = portfolioSnapshot?.multiPeriods ?? [];
  return (
    <ReportingBatchReportCard
      title={`${MdaReportType.PortfolioSnapshot} from ${getLocalDateTime(portfolioSnapshot.fromDate, DateTimeFormat.DayDate)} to ${getLocalDateTime(
        portfolioSnapshot.toDate,
        DateTimeFormat.DayDate
      )}`}
    >
      {multiPeriods.length > 0 && (
        <Grid item>
          <Autocomplete
            noOptionsText="No items to select"
            fullWidth
            multiple
            value={multiPeriods}
            options={multiPeriods}
            getOptionLabel={(option) => {
              const available = AvailablePeriods.find((period) => period.periodType.name === option.periodType);
              return available?.displayName ?? '';
            }}
            renderInput={(params) => <TextField {...params} margin="normal" variant="outlined" fullWidth />}
          />
        </Grid>
      )}
      {portfolioSnapshot.benchmarkId !== -1 && benchmarks && (
        <Grid item>
          <InputLabel id="benchmark">Benchmark</InputLabel>
          <TextField fullWidth disabled label={benchmarks.find((benchmark) => benchmark.id === portfolioSnapshot.benchmarkId)?.name} />
        </Grid>
      )}

      <Grid item container spacing={2}>
        <Grid item>
          <InputLabel id="performance-methodology">Performance Methodology</InputLabel>
          <WO2Select disabled labelId="performance-methodology" label="performance methodology" value={portfolioSnapshot.performanceMethodology}>
            <MenuItem value={PerformanceMethodology.TimeWeighted}>Time Weighted Return</MenuItem>
            <MenuItem value={PerformanceMethodology.ReturnOnAverage}>Return on Average Value</MenuItem>
          </WO2Select>
        </Grid>
        <Grid item>
          <InputLabel id="performance-data-type-id">Performance Data</InputLabel>
          <WO2Select fullWidth disabled labelId="performance-data-type-id" label="Performance Data" value={portfolioSnapshot.performanceDataType}>
            <MenuItem value={PerformanceDataType.Daily}>Daily</MenuItem>
            <MenuItem value={PerformanceDataType.Monthly}>Monthly</MenuItem>
          </WO2Select>
        </Grid>

        <GroupingOption value={portfolioSnapshot.groupOption} />
      </Grid>
      <Toggle checked={portfolioSnapshot.applyExclusionList} name="Apply Exclusion List" />
      <Toggle checked={portfolioSnapshot.includeAfterFeesReturn} name="Include After Fees Return" />
      <Toggle checked={portfolioSnapshot.excludeFrankingCredits} name="Exclude Franking Credits" />
      <Toggle checked={portfolioSnapshot.primaryBenchmark} name="Use Primary Benchmark" />
      <Toggle checked={portfolioSnapshot.secondaryBenchmark} name="Use Secondary Benchmark" />
    </ReportingBatchReportCard>
  );
};

const SecurityPerformanceCard = (securityPerformance: SecurityPerformance) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.SecurityPerformance} from ${getLocalDateTime(securityPerformance.fromDate, DateTimeFormat.DayDate)} to ${getLocalDateTime(
      securityPerformance.toDate,
      DateTimeFormat.DayDate
    )}`}
  >
    <Toggle checked={securityPerformance.excludeFrankingCredits} name="Exclude Franking Credits" />
    <Toggle checked={securityPerformance.showAnnualisedReturns} name="Show Annualised Returns" />
    <Toggle checked={securityPerformance.applyExclusionList} name="Apply Exclusion List" />
    <Toggle checked={securityPerformance.excludeCapitalIncomeReturns} name="Exclude Capital Income Returns" />
    <Toggle checked={securityPerformance.excludeMarketFxReturn} name="Exclude Market/FX Return" />
    <Grid item container spacing={2}>
      <Grid item>
        <InputLabel id="performance-methodology">Performance Methodology</InputLabel>
        <WO2Select disabled labelId="performance-methodology" label="performance methodology" value={securityPerformance.performanceMethodology}>
          <MenuItem value={PerformanceMethodology.TimeWeighted}>Time Weighted Return</MenuItem>
          <MenuItem value={PerformanceMethodology.ReturnOnAverage}>Return on Average Value</MenuItem>
        </WO2Select>
      </Grid>
      <Grid item>
        <InputLabel id="group-by-id">Group by</InputLabel>
        <WO2Select disabled label="group by" value={securityPerformance.groupBy}>
          <MenuItem value={GroupBySecurityPerformance.AssetClass}>Asset Class</MenuItem>
          <MenuItem value={GroupBySecurityPerformance.Gics}>GICS</MenuItem>
        </WO2Select>
      </Grid>
      <GroupingOption value={securityPerformance.groupOption} />
    </Grid>
  </ReportingBatchReportCard>
);

const IncomeReceivedCard = (incomeReceived: IncomeReceived) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.IncomeReceived} from ${getLocalDateTime(incomeReceived.fromDate, DateTimeFormat.DayDate)} to ${getLocalDateTime(
      incomeReceived.toDate,
      DateTimeFormat.DayDate
    )}`}
  >
    <Grid item container spacing={2}>
      <Grid item xs={12} sm={6}>
        <InputLabel id="income-received-group-by">Group by</InputLabel>
        <WO2Select disabled value={incomeReceived.groupBy}>
          <MenuItem value={GroupByIncomeReceived.Account}>Account</MenuItem>
          <MenuItem value={GroupByIncomeReceived.Date}>Date</MenuItem>
          <MenuItem value={GroupByIncomeReceived.IncomeType}>Income Type</MenuItem>
        </WO2Select>
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputLabel id="income-received-sort-by">Sort by</InputLabel>
        <WO2Select disabled value={incomeReceived.sortBy}>
          <MenuItem value={SortByIncomeReceived.Account}>Account</MenuItem>
          <MenuItem value={SortByIncomeReceived.DateAscending}>Date Ascending</MenuItem>
          <MenuItem value={SortByIncomeReceived.DateDescending}>Date Descending</MenuItem>
          <MenuItem value={SortByIncomeReceived.IncomeType}>Income Type</MenuItem>
        </WO2Select>
      </Grid>
    </Grid>
    <Toggle checked={incomeReceived.excludeCash} name="Exclude Cash" />
    <GroupingOption value={incomeReceived.groupOption} />
  </ReportingBatchReportCard>
);

const IncomeCard = (income: IncomeReport) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.IncomeReport} from ${getLocalDateTime(income.fromDate, DateTimeFormat.DayDate)} to ${getLocalDateTime(
      income.toDate,
      DateTimeFormat.DayDate
    )}`}
  >
    <Toggle checked={income.showOutstandingPaymentOnly} name="Show Outstanding Payment Only" />
    <Toggle checked={income.excludeCash} name="Exclude Cash" />
    <Grid item>
      <InputLabel id="show-receivables-as">Show Receivables As</InputLabel>
      <WO2Select disabled value={income.showReceivablesAs}>
        <MenuItem value={ShowReceivablesAsType.UnmappedAccruals}>Unmapped Accruals</MenuItem>
        <MenuItem value={ShowReceivablesAsType.PendingOnReportDate}>Pending on Report Date</MenuItem>
      </WO2Select>
    </Grid>
    <GroupingOption value={income.groupOption} />
  </ReportingBatchReportCard>
);

const ExpensesCard = (expenses: Expenses) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.Expenses} - ${getLocalDateTime(expenses.fromDate, DateTimeFormat.DayDate)} to ${getLocalDateTime(
      expenses.toDate,
      DateTimeFormat.DayDate
    )}`}
  >
    <Toggle checked={expenses.excludeExpenseType} name="Exclude Expense Type" />
    <GroupingOption value={expenses.groupOption} />
  </ReportingBatchReportCard>
);

const CashTransactionCard = (cashTransactions: CashTransactions) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.CashTransactions} - ${getLocalDateTime(cashTransactions.fromDate, DateTimeFormat.DayDate)} to ${getLocalDateTime(
      cashTransactions.toDate,
      DateTimeFormat.DayDate
    )}`}
  >
    <Grid item container spacing={2}>
      <Grid item>
        <InputLabel id="sort-by-id">Sort by</InputLabel>
        <WO2Select disabled value={cashTransactions.sortBy}>
          <MenuItem value={SortByCashTransactions.DateAscending}>Date Ascending</MenuItem>
          <MenuItem value={SortByCashTransactions.DateDescending}>Date Descending</MenuItem>
        </WO2Select>
      </Grid>
      <Toggle checked={cashTransactions.excludeAudValue} name="Exclude AUD Value" />
      <GroupingOption value={cashTransactions.groupOption} />
    </Grid>
  </ReportingBatchReportCard>
);

const TaxSummaryCard = (taxSummary: TaxSummary) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.TaxSummary} - ${getLocalDateTime(taxSummary.fromDate, DateTimeFormat.DayDate)} to ${getLocalDateTime(
      taxSummary.toDate,
      DateTimeFormat.DayDate
    )}`}
  >
    <YearTypeSelector value={taxSummary.yearType} />
  </ReportingBatchReportCard>
);

const InvestmentIncomeTaxCard = (investmentIncomeTax: InvestmentIncomeTax) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.InvestmentIncomeTax} - ${getLocalDateTime(investmentIncomeTax.fromDate, DateTimeFormat.DayDate)} to ${getLocalDateTime(
      investmentIncomeTax.toDate,
      DateTimeFormat.DayDate
    )}`}
  >
    <YearTypeSelector value={investmentIncomeTax.yearType} />
    <Toggle checked={investmentIncomeTax.excludeFxGainsLosses} name="Exclude FX Gains/Losses" />
  </ReportingBatchReportCard>
);

const CurrencyExposureCard = (currencyExposure: CurrencyExposure) => (
  <ReportingBatchReportCard title={`${MdaReportType.CurrencyExposure} - ${getLocalDateTime(currencyExposure.asAtDate, DateTimeFormat.DayDate)}`}>
    <Toggle checked={currencyExposure.applyExclusionList} name="Apply Exclusion List" />
    <Toggle checked={currencyExposure.includeDetailTable} name="Include Detail Table" />
    <GroupingOption value={currencyExposure.groupOption} />
  </ReportingBatchReportCard>
);

const ProjectedIncomeCashflowCard = (projectedIncomeCashflow: ProjectedIncomeCashflow) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.ProjectedIncomeCashflow} - ${getLocalDateTime(projectedIncomeCashflow.asAtDate, DateTimeFormat.DayDate)}`}
  >
    <GroupingOption value={projectedIncomeCashflow.groupOption} />
  </ReportingBatchReportCard>
);

const HoldingsVsTargetVarianceCard = (holdingsVsTargetVariance: HoldingsVsTargetVariance) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.HoldingsVsTargetVariance} - ${getLocalDateTime(holdingsVsTargetVariance.asAtDate, DateTimeFormat.DayDate)}`}
  >
    <Toggle checked={holdingsVsTargetVariance.applyExclusionList} name="Apply Exclusion List" />
    <Toggle checked={holdingsVsTargetVariance.includeSummary} name="Include Summary" />
  </ReportingBatchReportCard>
);

const ContributionsAndWithdrawalsCard = (contributions: ContributionsAndWithdrawals) => (
  <ReportingBatchReportCard
    title={`${MdaReportType.ContributionsAndWithdrawals} - ${getLocalDateTime(contributions.fromDate, DateTimeFormat.DayDate)} to ${getLocalDateTime(
      contributions.toDate,
      DateTimeFormat.DayDate
    )}`}
  >
    <GroupingOption value={contributions.groupOption} />
  </ReportingBatchReportCard>
);

const AssetAllocationCard = (assetAllocation: AssetAllocation) => (
  <ReportingBatchReportCard title={`${MdaReportType.AssetAllocation} - ${getLocalDateTime(assetAllocation.asAtDate, DateTimeFormat.DayDate)}`}>
    <Toggle checked={assetAllocation.includeAssetCategory} name="Include Asset Category" />
    <Toggle checked={assetAllocation.includeDetailTable} name="Include Detail Table" />
    <Toggle checked={assetAllocation.applyExclusionList} name="Apply Exclusion List" />

    <GroupingOption value={assetAllocation.groupOption} />
  </ReportingBatchReportCard>
);

const AssetClassPerformanceCard = (assetClassPerformance: AssetClassPerformance) => {
  const multiPeriods = assetClassPerformance?.multiPeriods ?? [];
  return (
    <ReportingBatchReportCard
      title={`${MdaReportType.AssetClassPerformance} - ${getLocalDateTime(
        assetClassPerformance.fromDate,
        DateTimeFormat.DayDate
      )} to ${getLocalDateTime(assetClassPerformance.toDate, DateTimeFormat.DayDate)}`}
    >
      {multiPeriods.length > 0 && (
        <Grid item>
          <Autocomplete
            noOptionsText="No items to select"
            fullWidth
            multiple
            disabled
            value={multiPeriods}
            options={multiPeriods}
            getOptionLabel={(option) => {
              const available = AvailablePeriods.find((period) => period.periodType.name === option.periodType);
              return available?.displayName ?? '';
            }}
            renderInput={(params) => <TextField {...params} margin="normal" variant="outlined" fullWidth />}
          />
        </Grid>
      )}
      <Toggle checked={assetClassPerformance.excludeFrankingCredits} name="Exclude Franking Credits" />
      <Toggle checked={assetClassPerformance.showAnnualisedReturns} name="Show Annualised Returns" />
      <Toggle checked={assetClassPerformance.applyExclusionList} name="Apply Exclusion List" />
      <Toggle checked={assetClassPerformance.excludeCapitalIncomeReturns} name="Exclude Capital Income Returns" />
      <Toggle checked={assetClassPerformance.includeAllocationChange} name="Include Allocation Change" />
      <Toggle checked={assetClassPerformance.includeReturnsSummary} name="Include Returns Summary" />
      <Toggle checked={assetClassPerformance.includeBenchmarkComparison} name="Include Benchmark Comparison" />

      <Grid item container spacing={2} alignItems="center">
        <Grid item>
          <InputLabel id="performance-methodology">Performance Methodology</InputLabel>
          <WO2Select disabled labelId="performance-methodology" label="performance methodology" value={assetClassPerformance.performanceMethodology}>
            <MenuItem value={PerformanceMethodology.TimeWeighted}>Time Weighted Return</MenuItem>
            <MenuItem value={PerformanceMethodology.ReturnOnAverage}>Return on Average Value</MenuItem>
          </WO2Select>
        </Grid>
        <GroupingOption value={assetClassPerformance.groupOption} />
      </Grid>
    </ReportingBatchReportCard>
  );
};

const DateRangeCard = (request: DateRangeCardProps) => (
  <ReportingBatchReportCard
    title={`${request.name} - ${getLocalDateTime(request.date.fromDate, DateTimeFormat.DayDate)} to ${getLocalDateTime(
      request.date.toDate,
      DateTimeFormat.DayDate
    )}`}
  />
);

const Toggle = ({ checked, name }: ToggleProps) => {
  return (
    <Grid item flexWrap="nowrap" container alignItems="center" spacing={1}>
      <Grid item>
        <WO2ToggleSwitch disabled checked={checked} color="primary" />
      </Grid>
      <Grid item>
        <Typography textOverflow="ellipsis" variant="h4">
          {name}
        </Typography>
      </Grid>
    </Grid>
  );
};

const GroupingOption = ({ value }: { value?: GroupConsolidatedOption }) => {
  return (
    <Grid item>
      <InputLabel id="group-type-id">Group Option</InputLabel>
      <WO2Select disabled labelId="group-type-id" label="Group Option" value={value}>
        <MenuItem value={GroupConsolidatedOption.Consolidated}>Consolidated</MenuItem>
        <MenuItem value={GroupConsolidatedOption.UnderlyingClients}>Underlying Clients</MenuItem>
        <MenuItem value={GroupConsolidatedOption.Both}>Both</MenuItem>
      </WO2Select>
    </Grid>
  );
};
